import React, { useState, useEffect, useCallback } from 'react';
import { SectionInstance } from './types';
import { generatePricingPage, getLeadForms } from '../../services/api';
import { Plus, Trash2, Loader2, Calculator, X, AlertCircle } from 'lucide-react';

interface PricingInputRow {
  label: string;
  unit: string;
  low: string;
  high: string;
  notes: string;
}

interface LeadForm {
  form_id: string;
  name: string;
}

interface PricingPageWizardProps {
  projectId: string;
  brandGuideId?: string | null;
  siteId?: string;
  onInsert: (sections: SectionInstance[]) => void;
  onClose: () => void;
}

const CURRENCIES = ['AUD', 'USD', 'GBP', 'EUR', 'NZD', 'CAD', 'SGD'];

const EMPTY_ROW = (): PricingInputRow => ({
  label: '', unit: '', low: '', high: '', notes: '',
});

const PricingPageWizard: React.FC<PricingPageWizardProps> = ({
  projectId,
  brandGuideId,
  onInsert,
  onClose,
}) => {
  const [service, setService]     = useState('');
  const [area, setArea]           = useState('');
  const [currency, setCurrency]   = useState('AUD');
  const [formId, setFormId]       = useState('');
  const [inputs, setInputs]       = useState<PricingInputRow[]>([EMPTY_ROW()]);
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState('');

  // Load lead forms for the project
  useEffect(() => {
    if (!projectId) return;
    getLeadForms(projectId)
      .then((forms: LeadForm[]) => setLeadForms(forms || []))
      .catch(() => setLeadForms([]));
  }, [projectId]);

  const addRow = useCallback(() => {
    setInputs(prev => [...prev, EMPTY_ROW()]);
  }, []);

  const removeRow = useCallback((i: number) => {
    setInputs(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  const updateRow = useCallback((i: number, key: keyof PricingInputRow, value: string) => {
    setInputs(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [key]: value };
      return next;
    });
  }, []);

  const handleInsert = async () => {
    if (!service.trim()) { setError('Please enter the service name.'); return; }
    if (!area.trim())    { setError('Please enter the area / location.'); return; }

    // Validate at least one input with a label
    const validInputs = inputs.filter(r => r.label.trim());
    if (validInputs.length === 0) { setError('Add at least one pricing input with a label.'); return; }

    setError('');
    setGenerating(true);

    try {
      const payload = {
        project_id: projectId,
        brand_guide_id: brandGuideId || null,
        service: service.trim(),
        area: area.trim(),
        currency,
        form_id: formId || null,
        pricing_inputs: validInputs,
      };

      const data = await generatePricingPage(payload);

      const sections: SectionInstance[] = data?.sections || [];
      if (sections.length === 0) {
        setError('Generation returned no sections. Please try again.');
        return;
      }

      onInsert(sections);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; '));
      } else {
        setError('Generation failed. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    /* Modal backdrop */
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--panel, rgba(255,255,255,0.95))',
        backdropFilter: 'blur(20px)',
        borderRadius: 16,
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflowY: 'auto',
        padding: 32,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(33,119,255,0.1)', borderRadius: 6,
              padding: '4px 10px', marginBottom: 8,
              fontSize: '0.75rem', color: '#2177FF', fontWeight: 600,
            }}>
              <Calculator size={13} /> Pricing Page Generator
            </div>
            <h2 style={{ fontSize: '1.375rem', fontWeight: 800, margin: 0, color: 'var(--text, #1e293b)' }}>
              Add Pricing Page
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)', marginTop: 4 }}>
              Generates a cost calculator + FAQ for "how much does {'{service}'} cost in {'{area}'}" searches.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: 'var(--text-secondary, #64748b)', borderRadius: 6, flexShrink: 0,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Service + Area */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, color: 'var(--text, #1e293b)' }}>
              Service <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={service}
              onChange={e => setService(e.target.value)}
              placeholder="e.g. house cleaning"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, color: 'var(--text, #1e293b)' }}>
              Area / Location <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={area}
              onChange={e => setArea(e.target.value)}
              placeholder="e.g. Sydney"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Currency */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, color: 'var(--text, #1e293b)' }}>
            Currency
          </label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} style={inputStyle}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Pricing inputs */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8, color: 'var(--text, #1e293b)' }}>
            Pricing Inputs <span style={{ color: '#ef4444' }}>*</span>
            <span style={{ fontWeight: 400, color: 'var(--text-secondary, #64748b)', marginLeft: 6 }}>
              — your real price figures (never shown to AI as fabricated; always grounded)
            </span>
          </label>

          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr 28px',
            gap: 8, marginBottom: 4,
            fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8',
            padding: '0 2px',
          }}>
            <span>Label</span>
            <span>Unit</span>
            <span>Low</span>
            <span>High</span>
            <span>Notes (optional)</span>
            <span />
          </div>

          {inputs.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr 28px', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={row.label}
                onChange={e => updateRow(i, 'label', e.target.value)}
                placeholder="Bedrooms"
                style={inputStyle}
              />
              <input
                type="text"
                value={row.unit}
                onChange={e => updateRow(i, 'unit', e.target.value)}
                placeholder="rooms"
                style={inputStyle}
              />
              <input
                type="text"
                value={row.low}
                onChange={e => updateRow(i, 'low', e.target.value)}
                placeholder="40"
                style={inputStyle}
              />
              <input
                type="text"
                value={row.high}
                onChange={e => updateRow(i, 'high', e.target.value)}
                placeholder="60"
                style={inputStyle}
              />
              <input
                type="text"
                value={row.notes}
                onChange={e => updateRow(i, 'notes', e.target.value)}
                placeholder="per room, standard clean"
                style={inputStyle}
              />
              <button
                onClick={() => removeRow(i)}
                disabled={inputs.length === 1}
                style={{
                  background: 'none', border: 'none', cursor: inputs.length === 1 ? 'default' : 'pointer',
                  color: inputs.length === 1 ? '#cbd5e1' : '#ef4444',
                  padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center',
                }}
                title="Remove row"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}

          <button
            onClick={addRow}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'none', border: '1px dashed #cbd5e1', borderRadius: 6,
              padding: '6px 14px', cursor: 'pointer', fontSize: '0.8125rem',
              color: '#64748b', marginTop: 4,
            }}
          >
            <Plus size={14} /> Add pricing input
          </button>
        </div>

        {/* Lead form */}
        <div style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, color: 'var(--text, #1e293b)' }}>
            Linked Lead Capture Form
            <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 6 }}>— optional, captures leads from calculator use</span>
          </label>
          <select value={formId} onChange={e => setFormId(e.target.value)} style={inputStyle}>
            <option value="">None (calculator only)</option>
            {leadForms.map(f => (
              <option key={f.form_id} value={f.form_id}>{f.name}</option>
            ))}
          </select>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
            padding: '12px 16px', marginBottom: 20, color: '#ef4444', fontSize: '0.875rem',
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            onClick={onClose}
            disabled={generating}
            style={{
              padding: '10px 20px', borderRadius: 6,
              border: '1px solid var(--stroke, rgba(15,23,42,0.10))',
              background: 'transparent', cursor: generating ? 'default' : 'pointer',
              fontSize: '0.9375rem', fontWeight: 600,
              color: 'var(--text-secondary, #64748b)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={generating}
            style={{
              padding: '10px 24px', borderRadius: 9999,
              background: generating ? '#94a3b8' : '#2177FF',
              border: 'none', cursor: generating ? 'default' : 'pointer',
              fontSize: '0.9375rem', fontWeight: 600, color: '#fff',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            {generating ? (
              <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
            ) : (
              <><Calculator size={16} /> Insert Pricing Page</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--stroke, rgba(15,23,42,0.12))',
  borderRadius: 8, fontSize: '0.9rem',
  background: 'var(--panel, #fff)',
  color: 'var(--text, #1e293b)',
  boxSizing: 'border-box',
};

export default PricingPageWizard;
